import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, CircularProgress, TextField } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CrmFeedback, CrmTrackView, crmApi } from '../../../features/crm/crmApi'
import { Hint, OutlineButton, Pill, TONES } from '../shared/crmUi'
import { FEEDBACKS, downscaleImage, hm } from '../shared/format'

// Шаг ментора: галочку ставит фото (и для шага 5 - результат), после проверки басшы - замок
const StepCard: React.FC<{ t: CrmTrackView; no: number; onZoom: (urls: string[]) => void }> = ({ t, no, onZoom }) => {
    const { formatMessage } = useIntl()
    const st = t.steps?.find((s) => s.stepNo === no)
    const photos = st?.photos ?? []
    const done = !!st?.doneAt
    const verified = !!st?.verifiedAt
    const isFb = no === 5

    const [addPhotos, { isLoading: uploading }] = crmApi.useAddStepPhotosMutation()
    const [deletePhoto] = crmApi.useDeleteStepPhotoMutation()
    const [setFeedback] = crmApi.useSetFeedbackMutation()
    const [fb, setFb] = useState<CrmFeedback | ''>(t.feedback || '')
    const [note, setNote] = useState(t.feedbackNote || '')
    useEffect(() => {
        setFb(t.feedback || '')
        setNote(t.feedbackNote || '')
    }, [t.id, t.feedback, t.feedbackNote])

    const upload = async (files: FileList | null) => {
        if (!files?.length || verified) return
        const body = new FormData()
        const small = await Promise.all(Array.from(files).map((f) => downscaleImage(f)))
        small.forEach((f) => body.append('files', f))
        try {
            await addPhotos({ trackId: t.id, stepNo: no, body }).unwrap()
        } catch {
            /* общий обработчик */
        }
    }

    const sendFeedback = (value: CrmFeedback, text: string) => {
        if (value === 'PROBLEM' && !text.trim()) return
        setFeedback({ trackId: t.id, feedback: value, note: text.trim() }).catch(() => undefined)
    }

    let status: React.ReactNode = null
    if (verified) status = <Pill tone="ok">✓ {formatMessage({ id: 'crmHeadVerifiedAt' }, { time: hm(st.verifiedAt) })}</Pill>
    else if (done) status = <Pill tone="wait">⏳ {formatMessage({ id: 'crmUnderReview' })}</Pill>
    else if (photos.length && isFb) status = <Pill>{formatMessage({ id: 'crmPickResult' })}</Pill>

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '28px minmax(0, 1fr)',
                gap: '10px',
                border: `1px solid ${done ? TONES.ok.border : ui.border}`,
                background: ui.card,
                borderRadius: '12px',
                padding: '12px',
            }}
        >
            <Box
                aria-hidden
                sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    border: `2px solid ${verified ? TONES.ok.text : done ? TONES.wait.text : ui.border}`,
                    background: verified ? TONES.ok.bg : 'transparent',
                    color: verified ? TONES.ok.text : TONES.wait.text,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 700,
                }}
            >
                {verified ? '✓' : done ? '⏳' : ''}
            </Box>
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box>
                    <Box sx={{ fontWeight: 700, fontSize: '14px' }}>
                        {no}. {formatMessage({ id: isFb ? 'crmStep_5_full' : `crmStep_${no}` })}
                    </Box>
                    <Box sx={{ fontSize: '12.5px', color: ui.muted }}>
                        {done
                            ? formatMessage({ id: 'crmStepDoneLine' }, { n: photos.length, time: hm(st.doneAt) })
                            : formatMessage({ id: `crmStepHint_${no}` })}
                    </Box>
                    {status && <Box sx={{ marginTop: '6px' }}>{status}</Box>}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                    {photos.map((p) => (
                        <Box key={p.id} sx={{ position: 'relative' }}>
                            <img
                                src={p.url}
                                alt=""
                                onClick={() => onZoom(photos.map((x) => x.url))}
                                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: `1px solid ${ui.border}` }}
                            />
                            {!verified && (
                                <Box
                                    component="button"
                                    type="button"
                                    aria-label={formatMessage({ id: 'crmDeletePhoto' })}
                                    title={formatMessage({ id: 'crmDeletePhoto' })}
                                    onClick={() => deletePhoto(p.id)}
                                    sx={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -8,
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: ui.ink,
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        lineHeight: '22px',
                                        padding: 0,
                                    }}
                                >
                                    ×
                                </Box>
                            )}
                        </Box>
                    ))}
                    {verified ? (
                        <Hint>🔒 {formatMessage({ id: 'crmLocked' })}</Hint>
                    ) : (
                        <OutlineButton component="label" size="small" disabled={uploading}>
                            {uploading ? <CircularProgress size={14} sx={{ marginRight: 1 }} /> : '📷 '}
                            {formatMessage({ id: photos.length ? 'crmAddMore' : 'crmUploadPhoto' })}
                            <input
                                hidden
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => {
                                    upload(e.target.files)
                                    e.target.value = ''
                                }}
                            />
                        </OutlineButton>
                    )}
                </Box>

                {isFb && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {FEEDBACKS.map((k) => {
                                const on = fb === k
                                const tone = k === 'OK' ? TONES.ok : k === 'PROBLEM' ? TONES.bad : k === 'QUESTION' ? TONES.wait : TONES.grey
                                return (
                                    <Box
                                        key={k}
                                        component="button"
                                        type="button"
                                        disabled={done}
                                        onClick={() => {
                                            setFb(k)
                                            sendFeedback(k, note)
                                        }}
                                        sx={{
                                            border: `1px solid ${on ? tone.text : ui.border}`,
                                            background: on ? tone.bg : ui.card,
                                            color: on ? tone.text : ui.ink,
                                            fontWeight: on ? 700 : 500,
                                            borderRadius: '10px',
                                            padding: '6px 12px',
                                            font: 'inherit',
                                            fontSize: '13px',
                                            cursor: done ? 'default' : 'pointer',
                                            opacity: done && !on ? 0.5 : 1,
                                        }}
                                    >
                                        {formatMessage({ id: `crmFbOption_${k}` })}
                                    </Box>
                                )
                            })}
                        </Box>
                        {(fb === 'PROBLEM' || fb === 'QUESTION') && (
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                disabled={done}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                onBlur={() => fb && sendFeedback(fb, note)}
                                placeholder={formatMessage({ id: fb === 'PROBLEM' ? 'crmProblemPlaceholder' : 'crmQuestionPlaceholder' })}
                            />
                        )}
                        {fb === 'PROBLEM' && !note.trim() && !done && (
                            <Box sx={{ fontSize: '12.5px', color: TONES.bad.text }}>{formatMessage({ id: 'crmProblemRequired' })}</Box>
                        )}
                        {fb === 'NO_REACH' && <Hint>{formatMessage({ id: 'crmNoReachHint' })}</Hint>}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default StepCard
